import React from "react";
import {
    Flag,
    Mountain,
    Wind,
    Hand,
    Ruler,
    Gauge,
    Sun,
    MapPin,
} from "lucide-react";

const RULES_BG =
    "https://images.unsplash.com/photo-1621005570352-6418df03796b?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzNzl8MHwxfHNlYXJjaHwxfHxnb2xmJTIwZ3JlZW4lMjBjbG9zZSUyMHVwfGVufDB8fHx8MTc4MTI2Mzc1MHww&ixlib=rb-4.1.0&q=85";

const ENV_RULES = [
    { label: "Pins", value: "Hard / Difficult", icon: Flag },
    { label: "Greens", value: "Hard", icon: Mountain },
    { label: "Fairways", value: "Hard", icon: Mountain },
    { label: "Stimp Meter", value: "14", icon: Gauge },
    { label: "Wind", value: "Variable", icon: Wind },
    { label: "Putting", value: "Manual", icon: Hand },
    { label: "Gimme Distance", value: "1.2 m", icon: Ruler },
    { label: "Daylight", value: "Standard", icon: Sun },
];

const TEE_BOXES = [
    { course: "Bethpage Black", men: "Blue", women: "Red" },
    { course: "Pebble Beach", men: "US Open", women: "Gold" },
    { course: "Oakmont", men: "Green", women: "Blue" },
    { course: "Pinehurst No. 2", men: "US Open", women: "Tufts" },
];

export const RulesGrid = () => {
    return (
        <section
            id="rules"
            className="relative border-b border-white/10 py-20 md:py-28 overflow-hidden"
            data-testid="rules-section"
        >
            <div className="absolute inset-0">
                <img src={RULES_BG} alt="" className="w-full h-full object-cover opacity-10" />
                <div className="absolute inset-0 bg-gradient-to-b from-[#0B1325] via-[#0B1325]/95 to-[#0B1325]" />
            </div>

            <div className="relative max-w-7xl mx-auto px-6 md:px-12">
                <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-12">
                    <div>
                        <div className="text-[11px] tracking-[0.4em] uppercase font-bold text-[#D4AF37] mb-3">
                            — Section 03 / Tournament Rules
                        </div>
                        <h2 className="font-display font-black uppercase text-white text-3xl sm:text-4xl lg:text-5xl tracking-tighter leading-none">
                            Mandatory
                            <br />
                            <span className="text-[#94A3B8]">Trackman Conditions.</span>
                        </h2>
                    </div>
                    <p className="text-[#94A3B8] text-sm max-w-md leading-relaxed">
                        Every round uploaded for prize consideration must be played under the
                        exact configuration below. Any deviation will void the submission.
                    </p>
                </div>

                {/* Environment Setup grid */}
                <div className="mb-12">
                    <div className="text-[10px] uppercase tracking-[0.4em] font-bold text-[#D4AF37] mb-4">
                        / Environment Setup
                    </div>
                    <div
                        className="grid grid-cols-2 md:grid-cols-4 gap-3"
                        data-testid="rules-environment-grid"
                    >
                        {ENV_RULES.map((r) => (
                            <div
                                key={r.label}
                                data-testid={`rule-${r.label.toLowerCase().replace(/\s+/g, "-")}`}
                                className="group relative bg-[#111827]/70 backdrop-blur-md border border-white/10 hover:border-[#D4AF37]/40 transition-colors p-5"
                            >
                                <div className="flex items-center justify-between mb-4">
                                    <div className="w-8 h-8 border border-[#D4AF37]/30 bg-[#D4AF37]/5 flex items-center justify-center">
                                        <r.icon size={14} className="text-[#D4AF37]" />
                                    </div>
                                    <span className="text-[10px] tracking-[0.3em] uppercase text-[#475569]">
                                        Mandatory
                                    </span>
                                </div>
                                <div className="text-[10px] uppercase tracking-[0.25em] text-[#94A3B8] mb-1">
                                    {r.label}
                                </div>
                                <div className="font-display font-bold text-white text-lg leading-tight">
                                    {r.value}
                                </div>
                            </div>
                        ))}
                    </div>
                </div>

                {/* Tee Box Matrix */}
                <div>
                    <div className="text-[10px] uppercase tracking-[0.4em] font-bold text-[#D4AF37] mb-4">
                        / Tee Box Matrix
                    </div>
                    <div
                        className="border border-white/10 bg-[#111827]/70 backdrop-blur-md overflow-x-auto"
                        data-testid="tee-box-matrix"
                    >
                        <table className="w-full">
                            <thead>
                                <tr className="text-[10px] uppercase tracking-[0.25em] font-bold text-[#94A3B8] border-b border-white/10 bg-white/[0.03]">
                                    <th className="py-4 pl-6 pr-3 text-left">Course</th>
                                    <th className="py-4 px-3 text-left">Men</th>
                                    <th className="py-4 px-3 text-left">Women</th>
                                    <th className="py-4 pr-6 pl-3 text-right">Status</th>
                                </tr>
                            </thead>
                            <tbody>
                                {TEE_BOXES.map((t) => (
                                    <tr
                                        key={t.course}
                                        data-testid={`tee-row-${t.course.toLowerCase().replace(/\s+/g, "-").replace(/\./g, "")}`}
                                        className="border-b border-white/5 last:border-0 hover:bg-white/[0.03] transition-colors"
                                    >
                                        <td className="py-5 pl-6 pr-3">
                                            <div className="flex items-center gap-3">
                                                <MapPin size={14} className="text-[#D4AF37]" />
                                                <span className="font-display font-bold text-white text-base">
                                                    {t.course}
                                                </span>
                                            </div>
                                        </td>
                                        <td className="py-5 px-3 text-sm text-white/90 font-mono-num">
                                            {t.men}
                                        </td>
                                        <td className="py-5 px-3 text-sm text-white/90 font-mono-num">
                                            {t.women}
                                        </td>
                                        <td className="py-5 pr-6 pl-3 text-right">
                                            <span className="inline-flex items-center gap-1.5 bg-[#FF5A00]/15 text-[#FF5A00] text-[10px] font-bold px-2 py-1 uppercase tracking-[0.2em] border border-[#FF5A00]/40">
                                                Locked
                                            </span>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>
        </section>
    );
};

export default RulesGrid;
